// src/lib/streamPlayer.js
// ─────────────────────────────────────────────────────────────────────────────
// Multi-protocol CCTV stream player helper.
//
// Apa yang bisa diputar browser secara native:
//   • HLS (.m3u8)        → lewat hls.js (sudah dipakai AutoScanPlayer)
//   • WebRTC / WHEP      → RTCPeerConnection native (latency paling rendah)
//   • File video         → mp4 / webm / mov langsung di <video src>
//
// Apa yang TIDAK bisa diputar browser:
//   • RTSP               → tidak ada satupun browser punya stack RTSP.
//                          Wajib lewat gateway (go2rtc / MediaMTX) yang
//                          mengubah RTSP jadi WebRTC (WHEP) atau HLS.
//
// Konfigurasi gateway (opsional, untuk auto-konversi RTSP):
//   VITE_STREAM_GATEWAY = https://gateway-anda  (base URL go2rtc / MediaMTX)
// ─────────────────────────────────────────────────────────────────────────────

const GATEWAY = (import.meta.env.VITE_STREAM_GATEWAY || '').replace(/\/+$/, '');

export function hasGateway() {
  return !!GATEWAY;
}

// Deteksi blokir mixed-content: halaman HTTPS tidak boleh memuat sumber HTTP
// (browser memblokir fetch/WebRTC ke http:// dari halaman https://). Umum
// terjadi saat kamera/gateway di IP privat LAN (mis. http://192.168.x.x:8889).
// localhost/127.0.0.1 dikecualikan karena browser mengizinkannya.
export function isMixedContentBlocked(rawUrl) {
  try {
    if (typeof window === 'undefined') return false;
    if (window.location.protocol !== 'https:') return false;
    const u = new URL(rawUrl, window.location.href);
    if (u.protocol !== 'http:') return false;
    if (/^(localhost|127\.0\.0\.1|\[::1\]|::1)$/i.test(u.hostname)) return false;
    return true;
  } catch {
    return false;
  }
}

// Deteksi tipe sumber dari URL.
// return: 'hls' | 'webrtc' | 'rtsp' | 'file' | 'unknown'
//
// Urutan penting: skema eksplisit → ekstensi file pada PATH (abaikan query &
// fragment) → heuristik path/endpoint. Ekstensi pasti (.m3u8/.mp4) harus
// menang di atas tebakan nama-path (mis. path yang kebetulan memuat "/whep/").
export function detectStreamType(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return 'unknown';
  const raw = rawUrl.trim();
  const u = raw.toLowerCase();

  // 1) Skema eksplisit — paling pasti
  if (/^rtsp:\/\//.test(u) || /^rtsps:\/\//.test(u)) return 'rtsp';
  if (/^webrtc:\/\//.test(u) || /^whep:\/\//.test(u)) return 'webrtc';

  // 2) Ekstensi file di pathname saja (buang query "?" & fragment "#")
  let path = u;
  try { path = new URL(raw).pathname.toLowerCase(); }
  catch { path = u.split('#')[0].split('?')[0]; }
  if (/\.m3u8$/.test(path)) return 'hls';
  if (/\.(mp4|webm|ogg|ogv|mov|m4v)$/.test(path)) return 'file';

  // 3) Heuristik endpoint/path (ambigu) — hanya setelah ekstensi pasti
  if (/\/api\/webrtc(\?|#|$)/.test(u) || /\/whep(\?|#|\/|$)/.test(u)) return 'webrtc';

  // 4) MediaMTX: port WebRTC default 8889 (mis. http://ip:8889/compress/)
  if (/^https?:\/\/[^/]+:8889(\/|$)/.test(u)) return 'webrtc';

  // 5) URL direktori http(s) (diakhiri "/") tanpa ekstensi → kemungkinan
  //    halaman WebRTC gateway (MediaMTX/go2rtc). WHEP endpoint diturunkan
  //    di toWhepEndpoint(). Bukan file/HLS karena itu sudah ditangani di atas.
  if (/^https?:\/\/[^/]+\/.+\/$/.test(u)) return 'webrtc';

  return 'unknown';
}

// Normalisasi skema custom WHEP ke https.
export function toWhepUrl(rawUrl) {
  return String(rawUrl || '')
    .replace(/^webrtc:\/\//i, 'https://')
    .replace(/^whep:\/\//i, 'https://');
}

// Turunkan endpoint WHEP signaling dari URL yang dimasukkan operator.
// - go2rtc:   {base}/api/webrtc?src=cam      → dipakai apa adanya
// - WHEP:     {base}/path/whep               → dipakai apa adanya
// - MediaMTX: {base}/compress/  (halaman)    → {base}/compress/whep
export function toWhepEndpoint(rawUrl) {
  const url = toWhepUrl(rawUrl).trim();
  const lu = url.toLowerCase();
  if (/\/api\/webrtc(\?|#|$)/.test(lu) || /\/whep(\?|#|$)/.test(lu)) return url;
  // Buang query/fragment, rapikan trailing slash, lalu tambahkan /whep.
  const [base] = url.split('#');
  const [path] = base.split('?');
  return path.replace(/\/+$/, '') + '/whep';
}

// Untuk RTSP: bangun URL WHEP (WebRTC) via gateway go2rtc.
// go2rtc menerima sumber langsung lewat query ?src= (URL-encoded).
export function gatewayWhepUrlForRtsp(rtspUrl) {
  if (!GATEWAY || !rtspUrl) return null;
  return `${GATEWAY}/api/webrtc?src=${encodeURIComponent(rtspUrl)}`;
}

// Untuk RTSP: URL HLS via gateway (fallback bila WebRTC gagal / jaringan ketat).
export function gatewayHlsUrlForRtsp(rtspUrl) {
  if (!GATEWAY || !rtspUrl) return null;
  return `${GATEWAY}/api/stream.m3u8?src=${encodeURIComponent(rtspUrl)}`;
}

// Best-effort: daftarkan stream RTSP ke go2rtc supaya ?src= dikenali.
// Idempoten di sisi go2rtc; kegagalan diabaikan (gateway lain mungkin
// tidak punya endpoint ini, tapi WHEP tetap dicoba).
async function tryRegisterGatewayStream(rtspUrl, signal) {
  if (!GATEWAY || !rtspUrl) return;
  try {
    await fetch(
      `${GATEWAY}/api/streams?src=${encodeURIComponent(rtspUrl)}&name=${encodeURIComponent(rtspUrl)}`,
      { method: 'PUT', signal },
    );
  } catch {
    /* gateway tidak support / offline — biarkan WHEP yang menentukan hasil */
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// WHEP WebRTC player.
// Standar WHEP: POST SDP offer (Content-Type: application/sdp) → terima SDP
// answer. Kompatibel go2rtc, MediaMTX, dan WHEP server lain.
//
// playWhep(videoEl, whepUrl, opts) → returns cleanup() function.
//   opts.onError(code)      code: 'cors' | 'gateway' | 'webrtc' | 'timeout' | null
//   opts.onConnected()      dipanggil saat peer connection 'connected'
//   opts.registerRtsp       string RTSP asli (untuk auto-register ke go2rtc)
// ─────────────────────────────────────────────────────────────────────────────
export function playWhep(videoEl, whepUrl, opts = {}) {
  const { onError, onConnected, timeoutMs = 15000, registerRtsp = null } = opts;
  let pc = null;
  let aborted = false;
  const ctrl = new AbortController();

  const timer = setTimeout(() => {
    if (!aborted && pc && pc.connectionState !== 'connected') {
      onError?.('timeout');
      cleanup();
    }
  }, timeoutMs);

  function cleanup() {
    if (aborted) return;
    aborted = true;
    clearTimeout(timer);
    try { ctrl.abort(); } catch { /* ignore */ }
    if (pc) {
      try { pc.getReceivers().forEach(r => r.track && r.track.stop()); } catch { /* ignore */ }
      try { pc.ontrack = null; pc.onconnectionstatechange = null; pc.close(); } catch { /* ignore */ }
      pc = null;
    }
    if (videoEl) { try { videoEl.srcObject = null; } catch { /* ignore */ } }
  }

  (async () => {
    try {
      if (registerRtsp) await tryRegisterGatewayStream(registerRtsp, ctrl.signal);
      if (aborted) return;

      pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        bundlePolicy: 'max-bundle',
      });
      pc.addTransceiver('video', { direction: 'recvonly' });
      pc.addTransceiver('audio', { direction: 'recvonly' });

      const remote = new MediaStream();
      pc.ontrack = (ev) => {
        if (aborted || !videoEl) return;
        remote.addTrack(ev.track);
        if (videoEl.srcObject !== remote) {
          videoEl.srcObject = remote;
          videoEl.play?.().catch(() => { /* autoplay block — user gesture nanti */ });
        }
      };
      pc.onconnectionstatechange = () => {
        if (!pc || aborted) return;
        if (pc.connectionState === 'connected') { clearTimeout(timer); onConnected?.(); }
        else if (pc.connectionState === 'failed') { onError?.('webrtc'); cleanup(); }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await waitIceGathering(pc, 3000); // non-trickle: cukup 1 POST

      const res = await fetch(toWhepEndpoint(whepUrl), {
        method: 'POST',
        headers: { 'Content-Type': 'application/sdp' },
        body: pc.localDescription.sdp,
        signal: ctrl.signal,
      });
      if (aborted) return;
      if (!res.ok) { onError?.('gateway'); cleanup(); return; }

      const answer = await res.text();
      if (aborted) return;
      if (!answer || !/v=0/.test(answer)) { onError?.('gateway'); cleanup(); return; }
      await pc.setRemoteDescription({ type: 'answer', sdp: answer });
    } catch (e) {
      if (aborted) return;
      // TypeError dari fetch = network/CORS block; AbortError = cleanup normal.
      if (e?.name === 'AbortError') return;
      onError?.(e?.name === 'TypeError' ? 'cors' : 'webrtc');
      cleanup();
    }
  })();

  return cleanup;
}

function waitIceGathering(pc, maxMs) {
  return new Promise((resolve) => {
    if (pc.iceGatheringState === 'complete') return resolve();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      pc.removeEventListener('icegatheringstatechange', check);
      resolve();
    };
    const check = () => { if (pc.iceGatheringState === 'complete') finish(); };
    pc.addEventListener('icegatheringstatechange', check);
    setTimeout(finish, maxMs);
  });
}
